/**
 * Shared vocabulary and safety semantics for on-device video compression.
 *
 * There are two compressors, and there will shortly be a second one in
 * production: the native Capacitor bridge (capacitor/video_compress.js, which
 * hands work to a native VideoCompressor plugin) and a WebCodecs implementation
 * for the MobiLoud Canvas WebView, where no native plugin exists. They compress
 * by completely different means and to deliberately different numbers — the
 * native one is mezzanine-grade (11-24 Mbps, a quality-preserving 4K->1440
 * downscale), the web one is upload thrift (~2.5 Mbps at 720p, matching where
 * the server's own transcode ladder ends).
 *
 * What they must NOT differ on is how they describe themselves. A skip reason
 * and a telemetry field name are a contract with analytics, not an
 * implementation detail: if the two compressors spell "already_efficient"
 * differently, or one reports out_bitrate_bps where the other reports
 * out_bitrate, then every question of the form "is compression helping?"
 * silently splits into two half-answers. This module owns that contract.
 *
 * WHAT IS DELIBERATELY NOT HERE: the trigger gate. The native compressor fires
 * on file size (>50MB); the web one fires on PREDICTED SECONDS SAVED (>=10s),
 * a figure it can only compute after demuxing the file — output bitrate times
 * duration, against the device's own measured encode speed and the client's
 * measured uplink. Those are different questions asked at different points in
 * the pipeline, and folding them into one parameterized gate would invent an
 * abstraction over a shape that is not actually shared. Each compressor
 * composes its own gate and borrows the content exemptions below.
 */

/**
 * Every skip_reason either compressor may emit. Frozen because these strings
 * are the analytics contract described above — a typo here is a silently
 * missing row in the skip-reason histogram, not a crash.
 *
 * Reasons are grouped by who decides them:
 *   - availability: the compressor cannot run at all
 *   - gate:         the source should not be compressed (per-consumer trigger)
 *   - content:      the source is exempt on its own merits (shared)
 *   - failure:      an attempt was made and did not produce a usable result
 *   - validation:   an output was produced and then rejected (shared)
 *
 * The gate reasons are per-consumer, so not every compressor emits every one:
 * the native bridge gates on file size and emits below_size_threshold, while
 * the web one gates on predicted seconds saved and emits not_worth_it or
 * no_throughput_prior. They live together anyway because a skip-reason
 * histogram is read across both, and a reason that exists in only one file is
 * exactly the kind that gets spelled two ways.
 */
export const SKIP_REASONS = Object.freeze({
  UNAVAILABLE: "unavailable",
  BELOW_SIZE_THRESHOLD: "below_size_threshold",
  // Too large to hold in memory while encoding. Kept distinct from UNAVAILABLE
  // so "this browser cannot compress at all" and "this one file is too big"
  // do not share a bucket — they are the difference between a device problem
  // and a ceiling worth raising, and the large-file companies are the whole
  // reason the feature exists.
  TOO_LARGE: "too_large",
  // The prediction ran and said compressing would not save enough time to be
  // worth the wait — either decided up front, or measured a few frames in and
  // abandoned. Distinct from a failure: nothing went wrong.
  NOT_WORTH_IT: "not_worth_it",
  // No throughput measurement on record, so there is nothing to predict a
  // saving against. Reported separately from not_worth_it because "we never
  // compress because nobody has a prior" and "we compress rarely because it
  // rarely helps" are completely different diagnoses.
  NO_THROUGHPUT_PRIOR: "no_throughput_prior",
  TOO_SHORT: "too_short",
  HDR_EXEMPT: "hdr_exempt_v1",
  HIGH_FPS_EXEMPT: "high_fps_exempt",
  ALREADY_EFFICIENT: "already_efficient",
  PROBE_FAILED: "probe_failed",
  COMPRESS_FAILED: "compress_failed",
  TIMEOUT: "timeout",
  NOT_SMALLER: "not_smaller",
  DURATION_MISMATCH: "duration_mismatch",
  ROTATION_LOST: "rotation_lost",
  AUDIO_LOST: "audio_lost",
  // An HDR source came back without its HDR signalling. Same class as
  // rotation_lost: the output is watchable but WRONG, and wrong in a way that
  // degrades gradually enough that nobody files a bug. Only reachable for a
  // compressor that opts into HDR at all; the rest still exempt it up front.
  HDR_LOST: "hdr_lost",
});

/**
 * Sentinel distinguishing "the timeout won the race" from a real result, which
 * is always a plain object and never this symbol.
 */
export const COMPRESSION_TIMEOUT = Symbol("video_compression_timeout");

/**
 * Race a unit of work against a hard timeout. Resolves to either the work's
 * result or the COMPRESSION_TIMEOUT sentinel.
 *
 * `startWork` is a thunk rather than a promise so that a SYNCHRONOUS throw from
 * it is treated exactly like a rejection — an encoder that throws on construct
 * must not escape as a different failure class than one that rejects. When the
 * timeout wins, the work promise is left running with a `.catch` safety net
 * attached up front, so its eventual rejection never surfaces as an unhandled
 * promise rejection.
 *
 * @param {() => Promise<any>} startWork
 * @param {number} timeoutMs
 * @returns {Promise<any|COMPRESSION_TIMEOUT>}
 */
export async function raceWithTimeout(startWork, timeoutMs) {
  let timeoutId;
  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(() => resolve(COMPRESSION_TIMEOUT), timeoutMs);
  });

  let workPromise;
  try {
    workPromise = Promise.resolve(startWork());
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
  workPromise.catch(() => {});

  try {
    return await Promise.race([workPromise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Measured output bitrate when the encoder reports one, else a size/duration
 * estimate. The estimate exists so telemetry stays populated against an older
 * native build that has not added the field yet; without it, "did the bitrate
 * actually come down?" becomes unanswerable for exactly the clients on stale
 * shells, who are the ones most likely to be having trouble.
 */
export function estimateOutputBitrateBps(out) {
  if (typeof out.bitrateBps === "number") {
    return out.bitrateBps;
  }
  if (
    typeof out.sizeBytes === "number" &&
    typeof out.durationMs === "number" &&
    out.durationMs > 0
  ) {
    return Math.round((out.sizeBytes * 8) / (out.durationMs / 1000));
  }
  return null;
}

/**
 * The flat telemetry object attached to every compression result.
 *
 * Always includes compressed/skip_reason/elapsed_ms; adds src_* once a probe
 * succeeded, and out_* whenever an attempt actually produced an output —
 * INCLUDING an output that then failed validation. Those near-misses are the
 * whole point of the out_* fields: a run that produced a smaller file and threw
 * it away for rotation_lost is the tripwire for "we are quietly degrading
 * quality", and it is invisible if only successes are reported.
 *
 * Never includes a raw file path.
 */
export function buildCompressionTelemetry({
  compressed,
  skipReason,
  startedAt,
  probe,
  out,
}) {
  const telemetry = {
    compressed,
    skip_reason: skipReason,
    elapsed_ms: Date.now() - startedAt,
  };

  if (probe) {
    telemetry.src_w = probe.widthPx;
    telemetry.src_h = probe.heightPx;
    telemetry.src_fps = probe.fps;
    telemetry.src_codec = probe.codec;
    telemetry.src_is_hdr = probe.isHdr;
    telemetry.src_bitrate_bps = probe.bitrateBps;
    telemetry.src_duration_ms = probe.durationMs;
    telemetry.src_has_audio = probe.hasAudio;
    telemetry.src_rotation = probe.rotationDegrees;
  }

  if (out) {
    telemetry.out_w = out.widthPx;
    telemetry.out_h = out.heightPx;
    telemetry.out_fps = out.fps;
    telemetry.out_codec = out.codec;
    telemetry.out_bitrate_bps = estimateOutputBitrateBps(out);
    telemetry.out_size_bytes = out.sizeBytes;
    telemetry.rotation_preserved = probe
      ? out.rotationDegrees === probe.rotationDegrees
      : null;
    // null rather than false when the output was not probed for HDR, so
    // "we did not look" stays distinguishable from "we looked and it was lost".
    telemetry.out_is_hdr = out.isHdr ?? null;
    telemetry.hdr_preserved =
      probe?.isHdr === true && typeof out.isHdr === "boolean"
        ? out.isHdr
        : null;
  }

  return telemetry;
}

/**
 * Post-compress validation, shared verbatim by both compressors.
 *
 * Returns the matching skip reason when the output must be discarded in favour
 * of the original, or null when it is safe to use. Order matters: not_smaller
 * is checked first because an output that is not smaller is worthless whatever
 * else is true of it, and checking it first keeps the histogram honest rather
 * than attributing the discard to whichever secondary defect happened to be
 * present too.
 *
 * rotation_lost is the one that must never be relaxed. Both server transcoders
 * run MediaConvert with rotate: 'AUTO', reading the container display matrix,
 * so dropping that matrix delivers every portrait phone video to the coach
 * rotated 90 degrees — and it is invisible until a human looks at it.
 */
export function validationFailureReason({ out, sizeBytes, probe }) {
  if (
    !(
      typeof out?.sizeBytes === "number" &&
      out.sizeBytes > 0 &&
      out.sizeBytes < sizeBytes
    )
  ) {
    return SKIP_REASONS.NOT_SMALLER;
  }
  // Tolerance is proportional but floored: a 2% drift on a 5s clip is 100ms,
  // which is below the noise of container duration rounding.
  const durationToleranceMs = Math.max(1000, probe.durationMs * 0.02);
  if (Math.abs(out.durationMs - probe.durationMs) > durationToleranceMs) {
    return SKIP_REASONS.DURATION_MISMATCH;
  }
  if (out.rotationDegrees !== probe.rotationDegrees) {
    return SKIP_REASONS.ROTATION_LOST;
  }
  if (probe.hasAudio && !out.hasAudio) {
    return SKIP_REASONS.AUDIO_LOST;
  }
  // Only meaningful for a compressor that chose to encode an HDR source rather
  // than exempt it. `out.isHdr` undefined means the caller does not probe for
  // it, and an unprobed output must not be failed on an unasked question.
  if (probe.isHdr === true && out.isHdr === false) {
    return SKIP_REASONS.HDR_LOST;
  }
  return null;
}

/**
 * Content-based exemptions: reasons to leave a source alone that depend on the
 * source itself rather than on why the caller wanted to compress it. Shared
 * because they are judgements about the video, not about the transport.
 *
 * `targetBitrateBps` is supplied by the caller rather than computed here — it
 * is the one genuinely divergent number (mezzanine vs upload thrift), and
 * "already efficient" means nothing except relative to the target you were
 * going to encode at.
 *
 * Returns a skip reason or null.
 */
export function contentExemptionSkipReason({
  probe,
  maxFps,
  maxDimension,
  targetBitrateBps,
  hdrCapable = false,
}) {
  // HDR is exempt unless the caller can encode it AS HDR. The danger was never
  // HDR itself, it was tone-mapping to SDR: the colour shifts visibly and the
  // source is retained, so it is a one-way door. A compressor that keeps the
  // signalling intact end to end is not doing that, and proves it by probing
  // its own output (see HDR_LOST) rather than by asserting it.
  //
  // Defaults to false so a caller that has not thought about HDR keeps today's
  // blanket exemption — the native bridge does not pass this.
  if (probe.isHdr === true && !hdrCapable) {
    return SKIP_REASONS.HDR_EXEMPT;
  }
  if (probe.fps > maxFps) {
    return SKIP_REASONS.HIGH_FPS_EXEMPT;
  }
  const longEdge = Math.max(probe.widthPx, probe.heightPx);
  if (longEdge <= maxDimension && probe.bitrateBps <= targetBitrateBps) {
    return SKIP_REASONS.ALREADY_EFFICIENT;
  }
  return null;
}
